import { useRef, useState } from "react";
import { Upload, Eye, CheckCheck, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { BTN_SOFT, BTN_SECONDARY, BTN_SM } from "./buttonStyles";
import { VerifyControls } from "@/components/guests/GovtDocRow";
import { useIdTypes } from "@/hooks/useIdTypes";

const MAX = 10 * 1024 * 1024;
const fileUrl = (u) => (u?.startsWith("/api") ? `${API_BASE}${u.slice(4)}` : u);

/** Upload ID Now / Mark as Collected controls for the Government ID pre-flight item. */
export function GovtIdActions({ check, guestId, stayId, hotelId, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);
  const [docType, setDocType] = useState("aadhaar");
  const idTypes = useIdTypes();
  const doc = check.document;

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX) { toast.error("File is larger than 10 MB"); return; }
    setPreview(file.type.startsWith("image/") ? URL.createObjectURL(file) : file.name);
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("doc_type", docType); fd.append("stay_id", stayId); if (hotelId) fd.append("hotel_id", hotelId);
      await api.post(`/guest/v1/guests/${guestId}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Government ID uploaded"); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); setPreview(null); }
    setBusy(null);
  };

  const mark = async () => {
    setBusy("mark");
    try { await api.post(`/pms/stays/${stayId}/preflight/mark`, { check_id: "government_id" }); toast.success("ID marked as collected"); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  if (check.status === "pass") {
    return (
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]" data-testid="govt-id-on-file">
        <span className="inline-flex items-center gap-1 rounded-md bg-[#DBFCEF] text-[#014751] px-2 py-1 font-semibold">
          <CheckCheck size={13} strokeWidth={1.75} />
          {doc?.collection_method === "marked_collected" ? `Marked collected by ${doc.collected_by_name || "desk"} at ${(doc.collected_at || "").slice(11, 16)}`
            : doc?.collection_method === "on_profile" ? `${doc.doc_type_label || "ID"} on profile ${doc.number_masked || ""} · verified${doc.verified_by_name ? ` by ${doc.verified_by_name}` : ""}`
            : `${doc?.doc_type_label || "Government ID"} on file · verified${doc?.verified_by_name ? ` by ${doc.verified_by_name}` : ""}`}
        </span>
        {doc?.file_url && <a href={fileUrl(doc.file_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#014751] hover:underline" data-testid="govt-id-view"><Eye size={13} />View</a>}
        {doc?.collection_method === "uploaded" && <button type="button" onClick={() => inputRef.current?.click()} className="font-semibold text-[#3F5257] hover:underline" data-testid="govt-id-reupload">Re-upload</button>}
        <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="govt-id-file-input" />
      </div>
    );
  }
  if (check.action === "verify_id" && doc) {
    return (
      <div className="space-y-2" data-testid="govt-id-unverified">
        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="inline-flex items-center gap-1 rounded-md bg-[#FEF3C7] text-[#78530E] px-2 py-1 font-semibold">
            <ShieldAlert size={13} strokeWidth={1.75} />{doc.doc_type_label || "ID"} {doc.collection_method === "on_profile" ? `on profile ${doc.number_masked || ""}` : "uploaded"} · not yet verified
          </span>
          {doc.file_url && <a href={fileUrl(doc.file_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#014751] hover:underline" data-testid="govt-id-view"><Eye size={13} />View</a>}
        </div>
        <VerifyControls guestId={guestId} docId={doc.id} doc={doc} label={doc.doc_type_label || "ID"} onChanged={onChanged} />
        <div className="text-[11px] text-[#97A6A9]">Or upload a different document: <button type="button" onClick={() => inputRef.current?.click()} className="font-semibold text-[#3F5257] hover:underline" data-testid="govt-id-upload-btn">Upload ID Now</button></div>
        <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="govt-id-file-input" />
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {preview && (
        <div className="flex items-center gap-2 text-[12px] text-[#3F5257]" data-testid="govt-id-preview">
          {preview.startsWith("blob:") ? <img src={preview} alt="ID preview" className="h-12 w-16 object-cover rounded border border-[#D5E6E8]" /> : <span className="mono">{preview}</span>}
          {busy === "upload" && <Loader2 size={14} className="animate-spin" />}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="govt-id-type-select"
          className="h-9 rounded-lg border border-[#D5E6E8] bg-white px-2 text-[12.5px] text-[#1A2425]">
          {idTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <button type="button" disabled={!!busy} onClick={() => inputRef.current?.click()} data-testid="govt-id-upload-btn"
          className={`${BTN_SOFT} ${BTN_SM}`}>
          {busy === "upload" ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} strokeWidth={1.75} />}Upload ID Now
        </button>
        <button type="button" disabled={!!busy} onClick={mark} data-testid="govt-id-mark-btn"
          className={`${BTN_SECONDARY} ${BTN_SM}`}>
          {busy === "mark" ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} strokeWidth={1.75} />}Mark as Collected
        </button>
        <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="govt-id-file-input" />
      </div>
      <div className="text-[11px] text-[#97A6A9]">JPG, PNG or PDF · max 10 MB</div>
    </div>
  );
}
